import { Injectable } from '@angular/core';
import { datadogLogs } from '@datadog/browser-logs';
import { environment } from 'src/environments/environment';
import { UserService } from '../user/user.service';
import { VersionService } from '../version/version.service';

type ZenityAttributes = {
  zenity_attributes: {
    [key: string]: string;
  };
};

@Injectable({
  providedIn: 'root',
})
/**
 * Logger service
 * It will handle all the logs created by the web app.
 */
export class LoggerService {
  private readonly _withDD: boolean;
  private readonly _withConsole: boolean;

  constructor(
    private readonly _userService: UserService,
    private readonly _versionService: VersionService
  ) {
    this._withDD = environment.dd.enabled;
    this._withConsole = !environment.production;

    if (this._withDD) {
      const version = this._versionService.version;
      datadogLogs.init({
        clientToken: environment.dd.clientToken,
        site: 'datadoghq.eu',
        forwardErrorsToLogs: environment.dd.forwardErrorsToLogs,
        sessionSampleRate: 100,
        env: environment.dd.env,
        service: environment.dd.service,
        version: version,
      });

      this.debug('Datadog initialized with version: ' + version);
    }
  }

  public debug(message: string, attributes: object = {}): void {
    if (environment.production) return;
    const allAttributes = this._fillAttributes(attributes);

    if (this._withDD) {
      datadogLogs.logger.debug(message, allAttributes);
    }

    if (this._withConsole) {
      console.debug(message, allAttributes);
    }
  }

  public info(message: string, attributes: object = {}): void {
    const allAttributes = this._fillAttributes(attributes);

    if (this._withDD) {
      datadogLogs.logger.info(message, allAttributes);
    }

    if (this._withConsole) {
      console.log(message, allAttributes);
    }
  }

  public warn(message: string, attributes: object = {}): void {
    const allAttributes = this._fillAttributes(attributes);

    if (this._withDD) {
      datadogLogs.logger.warn(message, allAttributes);
    }

    if (this._withConsole) {
      console.warn(message, allAttributes);
    }
  }

  public error(message: string, attributes: object = {}, error?: Error): void {
    if (this._withDD) {
      datadogLogs.logger.error(message, attributes, error);
    }

    if (this._withConsole) {
      console.error(message, attributes, error);
      alert(`Error found, check developer tools console for more info: ${message}`);
    }
  }

  private _fillAttributes(attributes: object): ZenityAttributes {
    const dpr = window.devicePixelRatio || 1;
    const must_attributes = {
      zenity_account_id: this._userService.zenityAccountId,
      screen_resolution: `${window.innerWidth}x${window.innerHeight}`,
      physical_resolution: `${Math.round(window.innerWidth * dpr)}x${Math.round(window.innerHeight * dpr)}`,
      device_pixel_ratio: `${dpr}`,
    };

    return { zenity_attributes: { ...attributes, ...must_attributes } };
  }
}
